import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { Redo2 } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { 
  getLastUndoneOperation, 
  clearLastUndoneOperation, 
  hasRedoableOperation,
  type UndoOperation 
} from "@/lib/undoTracking";

interface RedoButtonProps {
  userId: string;
}

export const RedoButton = ({ userId }: RedoButtonProps) => {
  const { t } = useTranslation();
  const [showButton, setShowButton] = useState(false);
  const [isRedoing, setIsRedoing] = useState(false);
  const [mobileBackdropActive, setMobileBackdropActive] = useState(false);
  const queryClient = useQueryClient();

  // Listen for mobile backdrop state changes
  useEffect(() => {
    const handleBackdropState = (e: CustomEvent) => {
      setMobileBackdropActive(e.detail.active);
    };
    
    window.addEventListener('mobile-backdrop-state', handleBackdropState as EventListener);
    return () => window.removeEventListener('mobile-backdrop-state', handleBackdropState as EventListener);
  }, []);

  // Check for redoable operations
  useEffect(() => {
    const checkRedo = () => {
      const hasRedo = hasRedoableOperation();
      setShowButton(hasRedo);
    };

    // Check immediately
    checkRedo();

    // Listen for tracking events
    const handleTracked = () => checkRedo();
    const handleCleared = () => setShowButton(false);
    // Also listen for undo events which might create a redo opportunity
    const handleUndoCleared = () => checkRedo();

    window.addEventListener('redo-operation-tracked', handleTracked);
    window.addEventListener('redo-operation-cleared', handleCleared);
    window.addEventListener('undo-operation-cleared', handleUndoCleared);

    // Check periodically (every 10 seconds) to hide button after timeout
    const interval = setInterval(checkRedo, 10000);

    return () => {
      window.removeEventListener('redo-operation-tracked', handleTracked);
      window.removeEventListener('redo-operation-cleared', handleCleared);
      window.removeEventListener('undo-operation-cleared', handleUndoCleared);
      clearInterval(interval);
    };
  }, []);

  const handleRedo = async () => {
    const operation = getLastUndoneOperation();
    if (!operation) {
      toast.error(t('notifications.toast.no_recent_operation_to_redo'));
      return;
    }

    // Check if operation belongs to current user
    if (operation.userId !== userId) {
      toast.error(t('notifications.toast.cannot_redo_another_user'));
      clearLastUndoneOperation();
      return;
    }

    setIsRedoing(true);

    try {
      if (operation.type === 'metric_value_update') {
        await redoMetricValueUpdate(operation);
      }
      // Note: custom_metric_created and builtin_metric_enabled are not redoable

      clearLastUndoneOperation();
      toast.success(t('notifications.toast.redone_successfully'));
    } catch (error) {
      console.error('[RedoButton] Redo failed:', error);
      toast.error(t('notifications.toast.failed_to_redo_operation'));
    } finally {
      setIsRedoing(false);
    }
  };

  const redoMetricValueUpdate = async (operation: UndoOperation) => {
    if (!operation.metricUpdates) return;

    // Build the update object for all metrics - restore to newValue (what was undone)
    const updates: Record<string, number | null> = {};
    
    for (const update of operation.metricUpdates) {
      // The newValue in the redo operation is what the value WAS before the undo
      updates[update.metricKey] = update.newValue;
    }

    // Update the daily entry
    const { error } = await supabase
      .from("daily_entries")
      .update(updates)
      .eq("user_id", operation.userId)
      .eq("entry_date", operation.entryDate);

    if (error) throw error;

    // Invalidate relevant queries
    queryClient.invalidateQueries({ queryKey: ["daily-entry", operation.userId, operation.entryDate] });
    queryClient.invalidateQueries({ queryKey: ["daily-entries", operation.userId] });
  };

  // Hide button if mobile backdrop is active or if there's no operation to redo
  if (!showButton || mobileBackdropActive) return null;

  return (
    <Button
      onClick={handleRedo}
      disabled={isRedoing}
      variant="ghost"
      size="sm"
      className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground opacity-50 animate-in fade-in slide-in-from-bottom-2 duration-300"
      title={t('common.redo')}
    >
      <Redo2 className="h-4 w-4" />
    </Button>
  );
};
